import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Grid,
    Alert,
    CircularProgress,
    Button,
    Paper,
    Card,
    CardContent,
    CardActions,
    Chip,
    IconButton,
} from '@mui/material';
import {
    FavoriteBorder as EmptyHeartIcon,
    Favorite as FilledHeartIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import marketplaceService from '../services/marketplaceService';

const FavoritesPage = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Fetch saved components
    const { data: savedItems = [], isLoading, error } = useQuery({
        queryKey: ['savedListings'],
        queryFn: () => marketplaceService.getSaved(),
    });

    // Remove from saved mutation
    const removeMutation = useMutation({
        mutationFn: (listingId) => marketplaceService.removeSaved(listingId),
        onSuccess: () => {
            queryClient.invalidateQueries(['savedListings']);
        },
    });

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">Failed to load favorites. Please try again.</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom fontWeight={600}>My Favorites</Typography>
                <Typography variant="body1" color="text.secondary">
                    {savedItems.length} saved {savedItems.length === 1 ? 'item' : 'items'}
                </Typography>
            </Box>

            {/* Empty State */}
            {savedItems.length === 0 && (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 4, bgcolor: '#fafafa' }}>
                    <EmptyHeartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" gutterBottom fontWeight={600}>No Favorites Yet</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Start browsing and save components you like!
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/parts')}
                        sx={{ borderRadius: 3 }}
                    >
                        Browse Marketplace
                    </Button>
                </Paper>
            )}

            {/* Favorites Grid */}
            {savedItems.length > 0 && (
                <>
                    <Grid container spacing={3}>
                        {savedItems.map((item) => {
                            // The /saved API returns objects; the shape can vary depending
                            // on backend version — use defensive access with fallbacks.
                            const listingId = item.listing_id ?? item.id;
                            const mpn = item.mpn ?? item.listing?.mpn ?? '—';
                            const manufacturer = item.manufacturer ?? item.listing?.manufacturer ?? '—';
                            const price = parseFloat(item.price ?? item.listing?.price ?? 0);
                            const availableQty = item.available_quantity ?? item.listing?.available_quantity ?? 0;
                            const condition = item.condition ?? item.listing?.condition ?? 'new';
                            const category = item.category ?? item.listing?.category ?? '';
                            const componentId = item.component_id ?? item.id ?? listingId;

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={listingId}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>
                                                    {mpn}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => removeMutation.mutate(listingId)}
                                                    disabled={removeMutation.isPending}
                                                    title="Remove from favorites"
                                                >
                                                    <FilledHeartIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {manufacturer}
                                            </Typography>
                                            {category && (
                                                <Chip label={category} size="small" variant="outlined" sx={{ mb: 1 }} />
                                            )}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                                <Typography variant="h6" color="primary" fontWeight={700}>
                                                    ${price.toFixed(2)}
                                                </Typography>
                                                <Chip
                                                    label={availableQty > 0 ? `${availableQty} in stock` : 'Out of stock'}
                                                    size="small"
                                                    color={availableQty > 0 ? 'success' : 'default'}
                                                />
                                            </Box>
                                            <Chip label={condition} size="small" variant="outlined" sx={{ mt: 1 }} />
                                        </CardContent>
                                        <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                                            <Button
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<ViewIcon />}
                                                onClick={() => navigate(`/parts/${componentId}/${encodeURIComponent(mpn)}`)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                View Details
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Button variant="outlined" onClick={() => navigate('/parts')} sx={{ borderRadius: 3 }}>
                            Continue Browsing
                        </Button>
                    </Box>
                </>
            )}
        </Container>
    );
};

export default FavoritesPage;
